import {
  CheckBoxFieldv2,
  ExternalLink,
  LabraToolTipV2,
  MultiSelectV4,
  NoteColors,
  NoteV2,
} from '@labrav/react-components'
import React, { useEffect, useMemo } from 'react'
import { Typography } from '@mui/material'
import { FormikProps } from 'formik'
import {
  FlyoutCloudSettingsSectionsProps,
  SellerTypeValue,
} from '../../../modules/flyOutOnboarding/types'
import { useStyles } from './AccountConfiguration.styles'
import { getCurrencyLabelForDisplay } from '../../../utils/getCurrencyLabelForDisplay'
import { multiSelectData, sellerTypeConfig } from './constants'
import { useSelector } from '../../../store'
import { AccountData } from '../../../types/external'
import { FlyOutListingData } from '../../../types/flyout'
import { useLocation } from 'react-router-dom'

export const AccountConfiguration: React.FC<{
  sectionName: string
  isFormLocked: boolean
  formik:
    | FormikProps<FlyoutCloudSettingsSectionsProps>
    | FormikProps<FlyOutListingData>
}> = ({ sectionName, isFormLocked, formik }) => {
  const { classes } = useStyles()
  const { pathname } = useLocation()
  const { values, touched, errors, setFieldValue } = formik
  const accountData = useSelector<AccountData>(
    state => state.AccountData.user.partnerData!
  )
  const awsOnboardingSellerType = useSelector<string[] | undefined>(
    state =>
      state.flyOutOnboarding?.onboardingForm?.labraPartnerDetails
        ?.partnershipType
  )
  const awsSettingsSellerType = useSelector<string[] | undefined>(
    state => state.flyoutSellerInfo?.sellerType
  )
  const microsoftSellerType = useSelector<SellerTypeValue[]>(
    state => state.flyOutOnboarding?.microsoftOnboardingForm?.sellerType || []
  )
  const partnershipType = accountData?.partnershipType
  const isOnboardingRoute = pathname.split('/').includes('onboarding')
  const isInIframe = pathname.split('/').includes('external-apps')
  // Handle both nested (Microsoft) and flat (AWS) structures
  const isNestedStructure = sectionName !== ''
  // Determine if this is Microsoft flow (read-only currencies) vs AWS flow (editable currencies)
  const isMicrosoftFlow = sectionName !== '' && sectionName !== 'cloudSettings'
  const isAWSOnboardingFlow =
    sectionName === 'cloudSettings' && isOnboardingRoute
  const isAWSSettingsFlow =
    sectionName === 'cloudSettings' && !isOnboardingRoute
  const HideAwsSellerType = isInIframe && isAWSSettingsFlow

  const getSectionData = () => {
    if (isNestedStructure) {
      return (values as any)[sectionName] || {}
    }
    return values as any
  }
  // Get currencies based on context
  // - AWS Onboarding: cloudSettings.currencies
  // - AWS Settings: root level currencies
  // - Microsoft: nested section currencies
  const currenciesValue = isAWSOnboardingFlow
    ? getSectionData()?.currencies || []
    : isAWSSettingsFlow
      ? (values as any).currencies || []
      : getSectionData()?.currencies || []

  // Get sellerType based on context
  // - AWS Onboarding: labraPartnerDetails.partnershipType
  // - AWS Settings: root level sellerType (transformed to 'type' by patchSellerData action)
  // - Microsoft: nested section sellerType
  const sellerTypeValue = isAWSOnboardingFlow
    ? (values as any).labraPartnerDetails?.partnershipType || []
    : isAWSSettingsFlow
      ? (values as any).sellerType || []
      : getSectionData()?.sellerType || []
  // Helper function to check if a seller type is selected
  const isSellerTypeSelected = (value: SellerTypeValue): boolean => {
    // Both AWS and Microsoft: sellerType is an array
    return Array.isArray(sellerTypeValue) && sellerTypeValue.includes(value)
  }

  // Helper functions to convert between currency keys and display labels
  const convertKeyToLabel = (key: string): string => {
    return getCurrencyLabelForDisplay(key)
  }

  const convertLabelToKey = (label: string): string => {
    // Extract the currency key from label format "US Dollar | USD($)"
    const match = label.match(/\|\s*([A-Z]{3})\(/)
    return match ? match[1] : label
  }

  // Convert stored keys to display labels for MultiSelectV4
  const currenciesDisplayValues = useMemo(() => {
    return currenciesValue.map((key: string) => convertKeyToLabel(key))
  }, [currenciesValue])
  // Set default USD currency for AWS Onboarding if currencies is empty
  useEffect(() => {
    if (!isAWSOnboardingFlow) {
      return
    }

    if (currenciesValue && currenciesValue.length > 0) {
      return
    }

    setFieldValue('cloudSettings.currencies', ['USD'])
  }, [isAWSOnboardingFlow, currenciesValue, setFieldValue])

  // Set sellerType based on context-specific sellerType or derive from partnershipType
  useEffect(() => {
    // For Microsoft flow in onboarding route: directly derive from accountData.partnershipType
    // Always update to match partnershipType (don't skip if value exists)
    if (isMicrosoftFlow && isOnboardingRoute) {
      if (partnershipType) {
        // Determine seller type based on partnershipType (can be string or array)
        const partnershipTypeArray = Array.isArray(partnershipType)
          ? partnershipType
          : [partnershipType]

        // Map partnership types directly to seller types
        const sellerTypes: SellerTypeValue[] = []

        if (partnershipTypeArray.includes('TECHNOLOGY_PARTNER')) {
          sellerTypes.push('ISV')
        }

        if (partnershipTypeArray.includes('CONSULTING_PARTNER')) {
          sellerTypes.push('CP')
        }

        // Default to ISV if no partnership type matches
        const finalSellerTypes = sellerTypes.length > 0 ? sellerTypes : ['ISV']

        // Only update if different from current value
        const currentSorted = Array.isArray(sellerTypeValue)
          ? [...sellerTypeValue].sort()
          : []
        const finalSorted = [...finalSellerTypes].sort()
        const isDifferent =
          JSON.stringify(currentSorted) !== JSON.stringify(finalSorted)

        if (isDifferent) {
          setFieldValue(`${sectionName}.sellerType`, finalSellerTypes)
        }
      }
      return
    }

    // Only prefill if sellerType is currently empty for other flows
    if (sellerTypeValue && sellerTypeValue.length > 0) {
      return
    }

    // For Microsoft flow (non-onboarding): use microsoftSellerType if available
    if (
      isMicrosoftFlow &&
      microsoftSellerType &&
      microsoftSellerType.length > 0
    ) {
      setFieldValue(`${sectionName}.sellerType`, microsoftSellerType)
      return
    }
    // For AWS Onboarding flow: use awsOnboardingSellerType if available
    if (
      isAWSOnboardingFlow &&
      awsOnboardingSellerType &&
      awsOnboardingSellerType.length > 0
    ) {
      setFieldValue(
        'labraPartnerDetails.partnershipType',
        awsOnboardingSellerType
      )
      return
    }

    // For AWS Settings flow: use awsSettingsSellerType if available
    if (
      isAWSSettingsFlow &&
      awsSettingsSellerType &&
      awsSettingsSellerType.length > 0
    ) {
      setFieldValue('sellerType', awsSettingsSellerType)
      return
    }

    // Fallback: derive from partnershipType (from accountData)
    if (partnershipType) {
      // Determine seller type based on partnershipType (can be string or array)
      const partnershipTypeArray = Array.isArray(partnershipType)
        ? partnershipType
        : [partnershipType]

      // Map partnership types directly to seller types
      const sellerTypes: SellerTypeValue[] = []

      if (partnershipTypeArray.includes('TECHNOLOGY_PARTNER')) {
        sellerTypes.push('ISV')
      }

      if (partnershipTypeArray.includes('CONSULTING_PARTNER')) {
        sellerTypes.push('CP')
      }

      // Default to ISV if no partnership type matches
      const finalSellerTypes = sellerTypes.length > 0 ? sellerTypes : ['ISV']

      if (isAWSOnboardingFlow) {
        setFieldValue('labraPartnerDetails.partnershipType', finalSellerTypes)
      } else if (isAWSSettingsFlow) {
        setFieldValue('sellerType', finalSellerTypes)
      } else if (isMicrosoftFlow) {
        setFieldValue(`${sectionName}.sellerType`, finalSellerTypes)
      }
    }
  }, [
    partnershipType,
    sectionName,
    setFieldValue,
    isAWSOnboardingFlow,
    isAWSSettingsFlow,
    isMicrosoftFlow,
    isOnboardingRoute,
    awsOnboardingSellerType,
    awsSettingsSellerType,
    microsoftSellerType,
    sellerTypeValue,
  ])

  const getFieldPath = (fieldName: string) => {
    // For currencies in AWS Settings, use root level
    if (fieldName === 'currencies' && isAWSSettingsFlow) {
      return 'currencies'
    }
    return isNestedStructure ? `${sectionName}.${fieldName}` : fieldName
  }

  const onChangeSelect = (event: unknown) => {
    const displayLabels = (event as React.ChangeEvent<HTMLInputElement>).target
      .value as any
    // Convert display labels back to keys before storing
    const keys = Array.isArray(displayLabels)
      ? displayLabels.map((label: string) => convertLabelToKey(label))
      : []
    setFieldValue(getFieldPath('currencies'), keys)
  }

  const handleDelete = (displayLabel: string) => {
    const keyToDelete = convertLabelToKey(displayLabel)
    const filteredKeys = currenciesValue.filter(
      (key: string) => key !== keyToDelete
    )
    setFieldValue(getFieldPath('currencies'), filteredKeys)
  }

  const handleFormikSellerTypeValueChange = (key: string, value: boolean) => {
    const sellerTypeKeyAndValue: Record<string, SellerTypeValue> = {
      isSellerTypeISV: 'ISV',
      isSellerTypeCP: 'CP',
    }
    const sellerTypeKey: SellerTypeValue = sellerTypeKeyAndValue[key]

    // Both AWS and Microsoft: sellerType is an array
    const arrayValue = Array.isArray(sellerTypeValue) ? sellerTypeValue : []
    const updatedSellerType = value
      ? [...arrayValue, sellerTypeKey]
      : arrayValue.filter((pt: SellerTypeValue) => pt !== sellerTypeKey)
    return updatedSellerType
  }

  const handleSellerTypeChange = async (key: string, value: boolean) => {
    const updatedSellerType = handleFormikSellerTypeValueChange(key, value)

    // Set the correct field based on context
    if (isAWSOnboardingFlow) {
      //  : update labraPartnerDetails.partnershipType
      setFieldValue('labraPartnerDetails.partnershipType', updatedSellerType)
    } else if (isAWSSettingsFlow) {
      // AWS Settings: update root level sellerType (action transforms to 'type' for backend)
      setFieldValue('sellerType', updatedSellerType)
    } else {
      // Microsoft or other flows: update nested sellerType
      setFieldValue(getFieldPath('sellerType'), updatedSellerType)
    }
  }

  const getErrorPath = (fieldName: string) => {
    if (isNestedStructure) {
      return {
        touched: (touched as any)[sectionName]?.[fieldName],
        error: (errors as any)[sectionName]?.[fieldName],
      }
    }
    return {
      touched: (touched as any)[fieldName],
      error: (errors as any)[fieldName],
    }
  }

  const sellerTypeError = getErrorPath('sellerType')
  const sellerTypeHasError =
    sellerTypeError.touched && Boolean(sellerTypeError.error)
  const sellerTypeErrorText =
    sellerTypeError.touched && (sellerTypeError.error as string)
  return (
    <div
      className={classes.primaryContainer}
      data-testid="microsoft-account-configuration"
    >
      {!HideAwsSellerType && (
        <div className={classes.sellerTypeSection}>
          {sellerTypeConfig.map(item => (
            <div className={classes.choiceBox} key={item.id}>
              <CheckBoxFieldv2
                keyName={item.id}
                title={item.text}
                value={isSellerTypeSelected(item.value as SellerTypeValue)}
                onChangeBox={e => {
                  const isChecked =
                    typeof e === 'boolean'
                      ? e
                      : (e as React.ChangeEvent<HTMLInputElement>).target
                          .checked
                  handleSellerTypeChange(item.id, isChecked)
                }}
                disabled={isOnboardingRoute}
                isError={sellerTypeHasError}
                errorText={sellerTypeErrorText as string}
              />
              <Typography className={classes.choiceBoxText}>
                {item.description}
              </Typography>
            </div>
          ))}
        </div>
      )}
      <div className={classes.selectCurrencySection}>
        {isMicrosoftFlow && (
          <NoteV2
            description={
              <span>
                Select currencies that are already configured in Partner Center.
                Currency with incomplete setups cannot be used for offer
                creation.
              </span>
            }
            backgroundColor={NoteColors.YELLOW}
          />
        )}
        {(isAWSOnboardingFlow || isAWSSettingsFlow) && (
          <NoteV2
            description={
              <span>
                Currency must be fully set up in AMMP, otherwise offers and
                authorizations using it will fail.{' '}
                <ExternalLink
                  href={
                    'https://docs.aws.amazon.com/marketplace/latest/userguide/seller-account-registering.html'
                  }
                >
                  {' '}
                  Learn more{' '}
                </ExternalLink>{' '}
              </span>
            }
            backgroundColor={NoteColors.YELLOW}
          />
        )}
        <Typography className={classes.selectCurrencyHeading}>
          Choose a transaction currency for this account. You will receive
          payment in the selected currency.
        </Typography>
        <div className={classes.selectCurrencySelector}>
          {isMicrosoftFlow ? (
            <LabraToolTipV2
              tooltipPlacement="top"
              description="Right now, we are only supporting US Dollar (USD)."
            >
              <MultiSelectV4
                field={{
                  ...multiSelectData,
                  defaultValue: undefined,
                  isReadOnly: true,
                }}
                onChangeSelect={onChangeSelect}
                selectedValues={currenciesDisplayValues}
                handleDelete={handleDelete}
                chipDisplayLimit={3}
              />
            </LabraToolTipV2>
          ) : (
            <MultiSelectV4
              field={{
                ...multiSelectData,
                defaultValue: undefined,
                isReadOnly: isFormLocked,
              }}
              onChangeSelect={onChangeSelect}
              selectedValues={currenciesDisplayValues}
              handleDelete={handleDelete}
              chipDisplayLimit={3}
            />
          )}
        </div>
      </div>
    </div>
  )
}
